import React from "react";
import {
  Col,
  Row,
  UiBox,
  UiButton,
  UiCheckbox,
  UiSkeletonText,
  UiTabPanel,
  UiText,
  useBreakpointValue,
} from "~/components/elements";
import { useAppDispatch, useAppSelector } from "~/hooks";
import {
  getSelectingHeadshotStyles,
  selectImagesByGroup,
  setSelectAllCheckboxes,
  setShowGoToFavPopup,
} from "~/redux/dashboards/slice";
import { toggleFavoriteAll } from "~/redux/dashboards/thunk";
import { ArrowRightIcon, DownloadIcon, RedHeartIcon, ShareIcon } from "~/components/icons/SvgIcons";
import { requestOK } from "redux-thunk-kit";
import { HeadshotStyleResultApi } from "~/types/models/dashboard";
import ResultGalleryForHeadshotStyle from "~/pages/result-detail/components/ResultGalleryForHeadshotStyle";

interface AllHeadshotsTabPanelProps {
  jobId: string;
  loading: boolean;
  selectAllCheckboxValue: boolean;
  headshotsCount: { all: number; favourite: number };
  downloadLoading: boolean;
  goToTab: (tabName: string) => void;
  onHandleDownloadImages: () => void;
  onClickShare: () => void;
}

export const AllHeadshotsTabPanel: React.FC<AllHeadshotsTabPanelProps> = ({
  jobId,
  loading,
  selectAllCheckboxValue,
  headshotsCount,
  downloadLoading,
  goToTab,
  onHandleDownloadImages,
  onClickShare,
}) => {
  const dispatch = useAppDispatch();
  const vrTitle = useBreakpointValue<any>({ base: "title2", sm: "title1" });
  const jobItemData = useAppSelector(state => state.dashboard.jobItemData);
  const headshotStyleResults = useAppSelector(state => state.dashboard.headshotStyleResults);
  const selectingHeadshotStyles: HeadshotStyleResultApi[] = useAppSelector(getSelectingHeadshotStyles);
  const resultHeadshotsStyle = selectingHeadshotStyles.length ? selectingHeadshotStyles : headshotStyleResults;

  const onClickSelectAll = async (checked: boolean) => {
    dispatch(setSelectAllCheckboxes({ group: "all", isSelected: checked }));
    dispatch(selectImagesByGroup({ group: "all", isSelected: checked }));
    const resAction = await dispatch(toggleFavoriteAll({ data: { is_favorited: checked }, jobID: jobId }));
    if (!requestOK(resAction, toggleFavoriteAll)) {
      dispatch(setSelectAllCheckboxes({ group: "all", isSelected: !checked }));
      dispatch(selectImagesByGroup({ group: "all", isSelected: !checked }));
    }
  };

  return (
    <UiTabPanel>
      <Row justifyContent="space-between" align="center" mt={1} zIndex="3">
        <UiCheckbox isChecked={selectAllCheckboxValue} onChange={e => onClickSelectAll(e.target.checked)}>
          <UiText vr="body2" whiteSpace="nowrap">
            Favorite all photoshoots
          </UiText>
        </UiCheckbox>
        <Col
          flexDirection={{ base: "column", md: "row" }}
          gap={2}
          align="center"
          justifyContent={{ base: "center", md: "flex-end" }}
          flexWrap="wrap"
          pos={{ base: "fixed", md: "relative" }}
          bottom={0}
          left={0}
          right={0}
          zIndex="3"
          bg={{ base: "white", md: "transparent" }}
          pt={{ base: 4, md: 0 }}
          pb={{ base: 5, md: 0 }}
          px={4}
          display={{ base: headshotsCount.favourite > 0 ? "flex" : "none", md: "flex" }}
        >
          <UiButton
            hideFrom="md"
            size="sm"
            w="full"
            variant="outline"
            leftIcon={<RedHeartIcon boxSize={{ base: 5, sm: 6 }} />}
            borderColor="darkblue"
            bg="transparent"
            color="darkblue"
            _hover={{
              bg: "transparent",
              color: "darkblue",
              cursor: headshotsCount.favourite > 0 ? "pointer" : "not-allowed",
            }}
            onClick={() => {
              dispatch(setShowGoToFavPopup({ isShow: false }));
              goToTab("tab_favorite");
            }}
          >
            <Row align="center">
              <UiText vr="title1">Go to favorites ({headshotsCount.favourite})</UiText>
              <ArrowRightIcon boxSize={5} />
            </Row>
          </UiButton>

          <UiButton
            hideBelow="md"
            size="sm"
            w="auto"
            variant="outline"
            leftIcon={<DownloadIcon boxSize={6} dColor="#0B0D2B" />}
            borderColor="darkblue"
            bg="transparent"
            color="darkblue"
            _hover={{
              bg: "transparent",
              color: "darkblue",
              cursor: headshotsCount.favourite > 0 ? "pointer" : "not-allowed",
            }}
            onClick={onHandleDownloadImages}
            isLoading={downloadLoading}
          >
            Download Favorites
          </UiButton>

          <UiButton
            size="sm"
            w={{ base: "full", md: "auto" }}
            variant="outline"
            leftIcon={<ShareIcon boxSize={6} dColor="#FFFFFF" />}
            bg="darkblue"
            color="neutral.gray00"
            _hover={{
              bg: "darkblue",
              color: "neutral.gray00",
              cursor: headshotsCount.favourite > 0 ? "pointer" : "not-allowed",
            }}
            onClick={onClickShare}
          >
            Share Favorites
          </UiButton>
        </Col>
      </Row>
      {headshotsCount.favourite > 0 && (
        <Row
          mx={{ base: 4, sm: "auto" }}
          gap={2}
          align="center"
          justifyContent="space-between"
          pos={{ base: "fixed" }}
          bottom="1.8rem"
          left={0}
          right={0}
          bg="white"
          zIndex="2"
          borderRadius="2.5rem"
          maxW="25rem"
          p={3}
        >
          <Row align="center">
            <RedHeartIcon boxSize={{ base: 4, sm: 6 }} />
            <UiText ml={1} vr="body2">
              {headshotsCount.favourite} favorites selected
            </UiText>
          </Row>
          <UiButton
            size="sm"
            variant="outline"
            borderColor="navy"
            bg="transparent"
            color="navy"
            _hover={{ bg: "transparent", color: "navy" }}
            onClick={() => {
              dispatch(setShowGoToFavPopup({ isShow: false }));
              goToTab("tab_favorite");
            }}
          >
            <Row align="center">
              <UiText vr={vrTitle} color="navy">
                Go to favorites
              </UiText>
              <ArrowRightIcon boxSize={5} dColor="navy" />
            </Row>
          </UiButton>
        </Row>
      )}
      <UiSkeletonText noOfLines={{ base: 5, md: 12 }} skeletonHeight={8} isLoaded={!loading} minH="80vh" mt={8}>
        <Col pb="100px">
          {resultHeadshotsStyle?.map(selectingHeadshot => (
            <UiBox mb={8} key={selectingHeadshot.headshot_style_id}>
              <UiText vr="title1" mb={4}>
                {selectingHeadshot.headshot_style?.background_name}&nbsp;
                {jobItemData?.type !== "two_person" && (
                  <UiText as="span" fontWeight="normal" vr="title1" mb={4}>
                    ({selectingHeadshot.headshot_style?.outfit_name})
                  </UiText>
                )}
              </UiText>
              <ResultGalleryForHeadshotStyle galleryImages={selectingHeadshot.images} />
            </UiBox>
          ))}
        </Col>
      </UiSkeletonText>
    </UiTabPanel>
  );
};
